import * as React from 'react';
import DashedLine from 'react-native-dashed-line';
import Util from 'src/Util';
import Styles from '../../Styles';
import AVFormattedCurrency from '../AVFormattedCurrency';
import type {
  Funding,
  PurchaseDetailType,
  TransactionDetail,
} from 'src/types/TransactionDetail';
import {PixelRatio, Platform, ScrollView, StyleSheet, View} from 'react-native';
import AVText from '../AVText';
import AccountStore from 'src/stores/AccountStore';
import {WithLocalizedProps} from 'src/types/PropTypes';

import {getDescriber as getSnackDescriber} from 'src/components/screens/sendSnack/descriptor/sendasnack/DescriptorType';
import {getDescriber} from 'src/components/elements/account/descriptor/DescriptorType';

type FundDetailProps = WithLocalizedProps & {
  detail: Funding | TransactionDetail | PurchaseDetailType;
  handleEmailReceipt: (transId: string) => void;
  handleRefund: (transId: string) => void;
  previousRoute?: string;
};

const FundDetail = (props: FundDetailProps) => {
  const formattedDate = Util.formatDate(props.detail.DateString, 'LLL');
  return (
    <>
      <View style={styles.container}>
        <ScrollView style={styles.historyContainer}>
          <AVText
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm11}
            accessible={true}
            accessibilityLabel={`${props.strings.Labels.date} ${formattedDate}`}
            aria-label={`${props.strings.Labels.date} ${formattedDate}`}
            testID={'qa_transactionDate_txt'}
            style={[
              getDescriber()?.fundingDetailDate(),
              getSnackDescriber()?.snackDetailsBtnFontSTyle(),
            ]}
          >
            {formattedDate}
          </AVText>
          {props?.detail?.Location && (
            <AVText
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm11}
              accessible={true}
              accessibilityLabel={`${props.strings.Labels.location} ${props.detail.Location}`}
              aria-label={`${props.strings.Labels.location} ${props.detail.Location}`}
              style={[
                styles.location,
                getSnackDescriber()?.snackDetailsBtnFontSTyle(),
              ]}
            >
              {props.detail.Location}
            </AVText>
          )}
          <AVText
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm11}
            accessible={true}
            accessibilityLabel={`${props.strings.Labels.transaction_id}`}
            aria-label={`${props.strings.Labels.transaction_id}`}
            testID={'qa_transactionId_txt'}
            style={[
              getDescriber()?.fundingdetailTrans(),
              getSnackDescriber()?.snackDetailsBtnFontSTyle(),
            ]}
          >
            {props.detail.Id}
          </AVText>
          <DashedLine
            dashThickness={PixelRatio.roundToNearestPixel(2)}
            dashGap={PixelRatio.roundToNearestPixel(2)}
            style={styles.dashStyle}
            dashLength={PixelRatio.roundToNearestPixel(2)}
            dashColor={getDescriber().payDashedLine()}
          />
          <View style={[styles.row]}>
            <View style={[styles.leftView]}>
              <AVText
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm11}
                accessible={true}
                accessibilityLabel={props.strings.Labels.account_funding}
                aria-label={props.strings.Labels.account_funding}
                testID={'qa_accountFundingLabel_txt'}
                style={[
                  getDescriber()?.fundingdetailAccountFndgTxt(),
                  getSnackDescriber()?.snackDetailsBtnFontSTyle(),
                ]}
              >
                {props.strings.Labels.account_funding}
              </AVText>
            </View>
            {getDescriber().fundingDetailAccount(styles, props)}
          </View>

          <DashedLine
            dashGap={PixelRatio.roundToNearestPixel(2)}
            dashLength={PixelRatio.roundToNearestPixel(2)}
            dashThickness={PixelRatio.roundToNearestPixel(2)}
            dashColor={getDescriber().payDashedLine()}
            style={styles.dashStyle}
          />
          <View
            accessible={true}
            style={[getDescriber().fundingDetailContainer()]}
          >
            <View
              style={[Styles.Style.labelColumn, getDescriber().leftViewStyle()]}
            >
              <AVText
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm8}
                accessibilityLabel={props.strings.Labels.total}
                aria-label={props.strings.Labels.total}
                testID={'qa_totalValueLabel_txt'}
                style={[
                  getDescriber()?.fundingDetailTotal(),
                  getSnackDescriber()?.snackDetailsBtnFontSTyle(),
                ]}
              >
                {props.strings.Labels.total}
              </AVText>
            </View>
            <View
              style={[
                Styles.Style.amountColumn,
                getDescriber().rightViewStyle(),
              ]}
            >
              <AVFormattedCurrency
                aria-label={`$${props.detail.Amount}`}
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm8}
                testID={'qa_totalValue_txt'}
                style={[
                  getDescriber()?.fundingDetailTotal(),
                  getSnackDescriber()?.snackDetailsBtnFontSTyle(),
                ]}
                value={props.detail.Amount}
                currency={AccountStore.getCurrency()}
                accessibilityLabel={`$${props.detail.Amount}`}
              />
            </View>
          </View>
        </ScrollView>
        <View
          style={{
            height: Styles.Spacing.m5 + Styles.Spacing.m3,
            width: '100%',
          }}
        />

        {getDescriber().historyEmailReceiptBtn(
          props.handleEmailReceipt,
          styles,
          props.detail.Id,
          AccountStore.getRegion(),
          props.previousRoute?.includes('History'),
        )}
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: getDescriber()?.fundingContainerColor(),
    flex: 1,
  },
  dashStyle: {
    borderRadius: 100,
    height: PixelRatio.roundToNearestPixel(2),
    marginBottom: Styles.Spacing.m2,
    marginTop: Styles.Spacing.m2,
    overflow: 'hidden',
    width: '100%',
  },
  date: {
    fontSize: Styles.Fonts.f0,
    marginBottom: Styles.Spacing.m3,
  },
  disabledRefundButton: {
    opacity: 0.5,
  },
  historyContainer: {
    flex: 1,
    marginBottom: Styles.Spacing.m5,
    padding: Styles.Spacing.m3,
    paddingVertical: 32,
  },
  largeLabel: {
    fontSize: Styles.Fonts.f1,
  },
  location: {
    fontSize: Styles.Fonts.f1,
  },
  refundButton: {
    marginBottom: Styles.Spacing.m2,
    marginTop: Styles.Spacing.m3,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    flex: 1,
  },
  total: {
    color: Styles.black,
    fontSize: Styles.Fonts.f3,
    fontWeight: '300',
    marginBottom: Styles.Spacing.m1,
    marginTop: -1 * Styles.Spacing.m1,
  },
  trans: {
    color: Styles.lightGray,
    fontSize: Styles.Fonts.f0,
  },
  leftView: {
    flex: 0.7,
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  rightView: {
    flex: 0.3,
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
  },
  buttonSpacing: {
    marginStart: Styles.Spacing.m2,
    marginBottom: Styles.Spacing.m1,
  },
  historyEmailRcptBtn: {
    borderTopColor: '#066DFF',
    borderBottomColor: '#066DFF',
    borderLeftColor: '#066DFF',
    borderRightColor: '#066DFF',
    borderRadius: 32,
    borderWidth: Platform.OS === 'android' ? 1.2 : 1,
    paddingTop: 6,
    paddingBottom: 6,
    paddingLeft: 20,
    paddingRight: 20,
    bottom: 10,
    right: 10,
    overflow: 'hidden',
    alignSelf: 'flex-end',
  },
});
export default FundDetail;
